import type { FieldValues } from 'react-hook-form';
import type { SmartRule, TemplateResponseCore } from '@gcv/shared';
import { SmartRuleAction, SmartRuleOperator } from '@gcv/shared';

export const shouldDisplayFieldWithSmartRule = (
  smartRule: SmartRule | undefined,
  formValues: FieldValues,
  template?: TemplateResponseCore,
  /** result_id where the smart rule we are trying to compute lives. If a smart rule points to or targets a repeated section field,
   * we have to assume that the smart rule lives on the same repeated section or else it doesnt make sense.
   * Therefore, when we search the form for a smart rule target that lives on a repeated section,
   * we have to look at the fieldId_resultId composite key value where the result id is the same for the smart rule and its target */
  resultId?: string
) => {
  if (smartRule) {
    let result = true;

    smartRule.conditions.forEach((c) => {
      let targetFieldId = c.targetFieldId;
      //find the requirement where the target field id lives. if it lives on a repeated section,
      //we have to check the target field value on the result of the same field we are trying to hide or show
      const requirementOfTargetedField = Object.values(template?.custom_requirements ?? []).find(
        (requirement) => {
          return requirement.custom_section.fields.some((field) => field.id === c.targetFieldId);
        }
      );

      if (requirementOfTargetedField?.custom_section?.repeatable) {
        targetFieldId = `${c.targetFieldId}_${resultId}`;
      }

      switch (c.operator) {
        case SmartRuleOperator.IsEmpty:
          result =
            result &&
            (!formValues[targetFieldId] ||
              formValues[targetFieldId] === '' ||
              formValues[targetFieldId] === '--');
          break;

        case SmartRuleOperator.IsEqualTo:
          result =
            result &&
            (formValues[targetFieldId] === c.targetValue?.trim() ||
              (typeof formValues[targetFieldId] === 'string' &&
                formValues[targetFieldId].includes(';') &&
                formValues[targetFieldId].split(';').find((i: string) => i === c.targetValue?.trim())));
          break;

        case SmartRuleOperator.IsFilled:
          result =
            result &&
            formValues[targetFieldId] &&
            formValues[targetFieldId] !== '' &&
            formValues[targetFieldId] !== '--';
          break;

        case SmartRuleOperator.IsNotEqualTo:
          result =
            result &&
            (formValues[targetFieldId] !== c.targetValue?.trim() ||
              (typeof formValues[targetFieldId] === 'string' &&
                !formValues[targetFieldId].includes(c.targetValue?.trim())));
          break;

        default:
          break;
      }
    });

    if (smartRule.action === SmartRuleAction.Show) {
      return result;
    } else if (smartRule.action === SmartRuleAction.Hide) {
      return !result;
    }
  }

  return true;
};
